import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
	Download,
	FileText,
	Landmark,
	Percent,
	Receipt,
	Scale,
	ShoppingBag,
	TrendingDown,
	TrendingUp,
	Trophy,
	Truck,
	Wallet,
} from "lucide-react";
import { reportsAPI } from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { usePermission } from "@/hooks/usePermission";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Price } from "@/components/ui/price";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ReportTable } from "../components/ReportTable";
import { RankedBars } from "../components/RankedBars";
import { StatTile } from "../components/StatTile";
import { SignedColumns } from "../components/TrendChart";
import { Panel, ReportNote, ReportState, StatGrid, StatusBadge, useReportQuery } from "../components/ReportBlocks";
import { formatPercent, num, roundTo, share, type ReportColumn } from "../lib/columns";
import { downloadWorkbook, type SheetRows } from "../lib/export";
import { paymentMethodLabel, statusLabel } from "../lib/labels";
import { toYmd, type ReportBodyProps, type ReportFiltersParams } from "../types";

const sumBy = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((s, r) => s + pick(r), 0);

/** "2026-10" → "10/2026", matching the app's DD/MM/YYYY dates. */
const monthLabel = (key: string) => {
	const [y, m] = String(key).split("-");
	return m && y ? `${m}/${y}` : key;
};

/** Export button for the statement-style reports that are not a plain ReportTable. */
function SheetExportButton({ build, fileName }: { build: () => SheetRows; fileName: string }) {
	const { t, i18n } = useTranslation();
	const { toast } = useToast();
	const canExport = usePermission("reports.export");
	if (!canExport) return null;
	const onClick = () => {
		try {
			downloadWorkbook([{ name: fileName, rows: build() }], fileName, i18n.language);
			toast({ title: t("common.success"), description: t("reports.export.success") });
		} catch {
			toast({ title: t("common.error"), description: t("reports.export.error"), variant: "destructive" });
		}
	};
	return (
		<Button variant="outline" size="sm" onClick={onClick} className="print:hidden">
			<Download className="h-4 w-4 me-2" />
			{t("reports.export.excel")}
		</Button>
	);
}

/* ─────────────────────────────── Profit & loss ─────────────────────────────── */

export interface ProfitMonth {
	month: string;
	income: { sales: number; total: number };
	expenses: { returns: number; other: number; total: number };
	netProfit: number;
}

/** The profits endpoint builds one column per month; "all time" is read as the last 12 months. */
export function profitRange(params: ReportFiltersParams): { startDate: string; endDate: string; defaulted: boolean } {
	if (params.startDate && params.endDate) {
		return { startDate: params.startDate, endDate: params.endDate, defaulted: false };
	}
	const now = new Date();
	return {
		startDate: toYmd(new Date(now.getFullYear(), now.getMonth() - 11, 1)),
		endDate: toYmd(now),
		defaulted: true,
	};
}

export const loadProfits = (params: ReportFiltersParams) => {
	const range = profitRange(params);
	return reportsAPI.getProfitsReport(range.startDate, range.endDate, params.branchId) as Promise<{
		months?: ProfitMonth[];
		summary?: { totalIncome: number; totalExpenses: number; netProfit: number };
	}>;
};

/** Statement lines. Product cost is not tracked by the app, so there is no cost-of-goods line. */
export function profitStatementLines(t: TFunction) {
	return [
		{
			key: "sales",
			label: t("reports.pl.salesIncome"),
			value: (m: ProfitMonth) => num(m.income?.sales),
			minus: false,
		},
		{
			key: "returns",
			label: t("reports.pl.returns"),
			value: (m: ProfitMonth) => num(m.expenses?.returns),
			minus: true,
		},
		{
			key: "expenses",
			label: t("reports.pl.operatingExpenses"),
			value: (m: ProfitMonth) => num(m.expenses?.other),
			minus: true,
		},
		{
			key: "net",
			label: t("reports.pl.netProfit"),
			value: (m: ProfitMonth) => num(m.netProfit),
			minus: false,
			strong: true,
		},
	];
}

export function profitSheet(t: TFunction, months: ProfitMonth[]): SheetRows {
	return [
		[t("reports.pl.line"), ...months.map(m => monthLabel(m.month)), t("reports.table.total")],
		...profitStatementLines(t).map(l => [
			l.label,
			...months.map(m => roundTo(l.value(m), 2)),
			roundTo(
				sumBy(months, m => l.value(m)),
				2,
			),
		]),
	];
}

export function ProfitLossReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const range = profitRange(params);
	const query = useReportQuery("profit-loss", { range, branchId: params.branchId }, () => loadProfits(params));
	return (
		<ReportState query={query}>
			{data => {
				const months = data?.months ?? [];
				const income = num(data?.summary?.totalIncome);
				const costs = num(data?.summary?.totalExpenses);
				const net = num(data?.summary?.netProfit);
				const lines = profitStatementLines(t);
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.pl.totalIncome")}
								value={income}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={TrendingDown}
								label={t("reports.pl.totalCosts")}
								value={costs}
								format="money"
								currency={currency}
								hint={t("reports.pl.costsHint")}
							/>
							<StatTile
								icon={Scale}
								label={t("reports.pl.netProfit")}
								value={net}
								format="money"
								currency={currency}
								tone={net < 0 ? "negative" : "positive"}
							/>
							<StatTile
								icon={Percent}
								label={t("reports.pl.netMargin")}
								value={income ? share(net, income) : null}
								format="percent"
							/>
						</StatGrid>
						<Panel title={t("reports.pl.chartTitle")}>
							<SignedColumns
								ariaLabel={t("reports.pl.chartTitle")}
								currency={currency}
								data={months.map(m => ({
									label: monthLabel(m.month),
									title: monthLabel(m.month),
									value: num(m.netProfit),
								}))}
							/>
						</Panel>
						<Panel
							title={t("reports.pl.statement")}
							actions={<SheetExportButton fileName={exportName} build={() => profitSheet(t, months)} />}
						>
							<Table className="[&_td]:px-4 [&_th]:px-4">
								<TableHeader>
									<TableRow>
										<TableHead className="sticky start-0 bg-card text-start">
											{t("reports.pl.line")}
										</TableHead>
										{months.map(m => (
											<TableHead key={m.month} className="whitespace-nowrap text-end">
												<span dir="ltr">{monthLabel(m.month)}</span>
											</TableHead>
										))}
										<TableHead className="whitespace-nowrap text-end">
											{t("reports.table.total")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{lines.map(line => {
										const total = sumBy(months, m => line.value(m));
										return (
											<TableRow
												key={line.key}
												className={cn(line.strong && "bg-muted/40 font-bold")}
											>
												<TableCell className="sticky start-0 whitespace-nowrap bg-card">
													{line.minus ? `− ${line.label}` : line.label}
												</TableCell>
												{months.map(m => (
													<TableCell
														key={m.month}
														className={cn(
															"whitespace-nowrap text-end",
															line.strong && line.value(m) < 0 && "text-destructive",
														)}
													>
														<Price value={roundTo(line.value(m), 2)} currency={currency} />
													</TableCell>
												))}
												<TableCell
													className={cn(
														"whitespace-nowrap text-end font-semibold",
														line.strong && total < 0 && "text-destructive",
													)}
												>
													<Price value={roundTo(total, 2)} currency={currency} />
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
								<TableFooter>
									<TableRow>
										<TableCell className="sticky start-0 bg-card">
											{t("reports.pl.netMargin")}
										</TableCell>
										{months.map(m => (
											<TableCell key={m.month} className="text-end">
												<span dir="ltr">
													{num(m.income?.total)
														? formatPercent(share(num(m.netProfit), num(m.income?.total)))
														: "-"}
												</span>
											</TableCell>
										))}
										<TableCell className="text-end">
											<span dir="ltr">{income ? formatPercent(share(net, income)) : "-"}</span>
										</TableCell>
									</TableRow>
								</TableFooter>
							</Table>
						</Panel>
						<ReportNote>
							{range.defaulted ? `${t("reports.pl.defaultedRange")} ` : ""}
							{t("reports.notes.profitLoss")}
						</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── VAT ─────────────────────────────── */

export interface TaxData {
	sales?: { totalTax: number; totalAmount: number; totalTransactions: number };
	purchases?: { totalTax: number; totalAmount: number; totalTransactions: number };
	summary?: { netTax: number };
}

export const loadTax = (params: ReportFiltersParams) => reportsAPI.getTaxReport(params) as Promise<TaxData>;

/** VAT-return layout: each line as amount excluding VAT, VAT, and amount including VAT. */
export function vatLines(t: TFunction, data: TaxData | undefined) {
	const line = (key: string, label: string, d?: TaxData["sales"]) => ({
		key,
		label,
		gross: num(d?.totalAmount),
		vat: num(d?.totalTax),
		net: num(d?.totalAmount) - num(d?.totalTax),
		count: num(d?.totalTransactions),
	});
	return [
		line("sales", t("reports.vat.salesLine"), data?.sales),
		line("purchases", t("reports.vat.purchasesLine"), data?.purchases),
	];
}

export function vatSheet(t: TFunction, data: TaxData | undefined): SheetRows {
	const due = num(data?.sales?.totalTax) - num(data?.purchases?.totalTax);
	return [
		[
			t("reports.vat.line"),
			t("reports.vat.netAmount"),
			t("reports.vat.vat"),
			t("reports.vat.grossAmount"),
			t("reports.cols.invoices"),
		],
		...vatLines(t, data).map(l => [l.label, roundTo(l.net, 2), roundTo(l.vat, 2), roundTo(l.gross, 2), l.count]),
		[due >= 0 ? t("reports.vat.netDue") : t("reports.vat.netRefund"), "", roundTo(Math.abs(due), 2), "", ""],
	];
}

export function VatReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery("vat", params, () => loadTax(params));
	return (
		<ReportState query={query} tiles={3}>
			{data => {
				const output = num(data?.sales?.totalTax);
				const input = num(data?.purchases?.totalTax);
				const due = output - input;
				const dueLabel = due >= 0 ? t("reports.vat.netDue") : t("reports.vat.netRefund");
				return (
					<div className="space-y-4">
						<StatGrid className="xl:grid-cols-3">
							<StatTile
								icon={Receipt}
								label={t("reports.vat.output")}
								value={output}
								format="money"
								currency={currency}
								hint={t("reports.vat.outputHint")}
							/>
							<StatTile
								icon={ShoppingBag}
								label={t("reports.vat.input")}
								value={input}
								format="money"
								currency={currency}
								hint={t("reports.vat.inputHint")}
							/>
							<StatTile
								icon={Landmark}
								label={dueLabel}
								value={Math.abs(due)}
								format="money"
								currency={currency}
								tone={due >= 0 ? "warning" : "positive"}
								hint={t("reports.vat.netHint")}
							/>
						</StatGrid>
						<Panel
							title={t("reports.vat.summary")}
							actions={<SheetExportButton fileName={exportName} build={() => vatSheet(t, data)} />}
						>
							<Table className="[&_td]:px-4 [&_th]:px-4">
								<TableHeader>
									<TableRow>
										<TableHead className="text-start">{t("reports.vat.line")}</TableHead>
										<TableHead className="text-end">{t("reports.vat.netAmount")}</TableHead>
										<TableHead className="text-end">{t("reports.vat.vat")}</TableHead>
										<TableHead className="hidden text-end md:table-cell">
											{t("reports.vat.grossAmount")}
										</TableHead>
										<TableHead className="hidden text-end md:table-cell">
											{t("reports.cols.invoices")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{vatLines(t, data).map(l => (
										<TableRow key={l.key}>
											<TableCell className="whitespace-nowrap font-medium">{l.label}</TableCell>
											<TableCell className="whitespace-nowrap text-end">
												<Price value={roundTo(l.net, 2)} currency={currency} />
											</TableCell>
											<TableCell className="whitespace-nowrap text-end font-semibold">
												<Price value={roundTo(l.vat, 2)} currency={currency} />
											</TableCell>
											<TableCell className="hidden whitespace-nowrap text-end md:table-cell">
												<Price value={roundTo(l.gross, 2)} currency={currency} />
											</TableCell>
											<TableCell className="hidden text-end tabular-nums md:table-cell">
												{l.count}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
								<TableFooter>
									<TableRow>
										<TableCell className="font-bold">{dueLabel}</TableCell>
										<TableCell />
										<TableCell className="whitespace-nowrap text-end font-bold">
											<Price value={roundTo(Math.abs(due), 2)} currency={currency} />
										</TableCell>
										<TableCell className="hidden md:table-cell" />
										<TableCell className="hidden md:table-cell" />
									</TableRow>
								</TableFooter>
							</Table>
						</Panel>
						<ReportNote>{t("reports.notes.vat")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Expenses ─────────────────────────────── */

export function expenseColumns(t: TFunction, currency: string): ReportColumn<any>[] {
	return [
		{ key: "date", label: t("reports.cols.date"), value: e => e.expenseDate, kind: "date" },
		{ key: "reference", label: t("reports.cols.reference"), value: e => e.reference || "", hideOnMobile: true },
		{
			key: "category",
			label: t("reports.cols.category"),
			value: e => e.category?.name || t("reports.uncategorized"),
		},
		{ key: "description", label: t("reports.cols.description"), value: e => e.description || "" },
		{
			key: "branch",
			label: t("reports.cols.branch"),
			value: e => e.branch?.name || t("reports.companyWide"),
			hideOnMobile: true,
		},
		{
			key: "method",
			label: t("reports.cols.paymentMethod"),
			value: e => (e.paymentMethod ? paymentMethodLabel(t, e.paymentMethod) : ""),
			hideOnMobile: true,
		},
		{
			key: "amount",
			label: t("reports.cols.amount"),
			value: e => num(e.amount),
			kind: "money",
			currency,
			total: true,
		},
	];
}

export const loadExpenses = (params: ReportFiltersParams) =>
	reportsAPI.getExpensesReport(params).then((d: any) => (Array.isArray(d) ? d : []) as any[]);

export function ExpensesReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery("expenses", params, () => loadExpenses(params));
	return (
		<ReportState query={query}>
			{rows => {
				const total = sumBy(rows, e => num(e.amount));
				const byCategory = new Map<string, number>();
				for (const e of rows) {
					const name = e.category?.name || t("reports.uncategorized");
					byCategory.set(name, (byCategory.get(name) ?? 0) + num(e.amount));
				}
				const top = Array.from(byCategory.entries()).sort((a, b) => b[1] - a[1])[0];
				const largest = [...rows].sort((a, b) => num(b.amount) - num(a.amount))[0];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={Wallet}
								label={t("reports.kpi.totalExpenses")}
								value={total}
								format="money"
								currency={currency}
							/>
							<StatTile icon={FileText} label={t("reports.kpi.expenseEntries")} value={rows.length} />
							<StatTile
								icon={Trophy}
								label={t("reports.kpi.topExpenseCategory")}
								value={top ? top[1] : null}
								format="money"
								currency={currency}
								hint={top?.[0]}
							/>
							<StatTile
								icon={Receipt}
								label={t("reports.kpi.largestExpense")}
								value={largest ? num(largest.amount) : null}
								format="money"
								currency={currency}
								hint={largest?.description || largest?.category?.name}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.expensesByCategory")}
							currency={currency}
							showShare
							items={Array.from(byCategory.entries()).map(([name, value]) => ({
								key: name,
								label: name,
								value,
							}))}
						/>
						<ReportTable
							columns={expenseColumns(t, currency)}
							rows={rows}
							rowKey={e => e.id}
							rowHref={e => `/expenses?edit=${e.id}`}
							exportName={exportName}
							emptyIcon={Wallet}
							defaultSort={{ key: "date", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.expenses")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Purchases ─────────────────────────────── */

export function purchaseColumns(t: TFunction, currency: string): ReportColumn<any>[] {
	return [
		{ key: "reference", label: t("reports.cols.reference"), value: p => p.reference || `#${p.id}` },
		{ key: "date", label: t("reports.cols.date"), value: p => p.createdAt, kind: "date" },
		{ key: "supplier", label: t("reports.cols.supplier"), value: p => p.supplier?.name || "" },
		{ key: "branch", label: t("reports.cols.branch"), value: p => p.branch?.name || "", hideOnMobile: true },
		{
			key: "status",
			label: t("reports.cols.status"),
			value: p => statusLabel(t, "purchases.statuses", p.status),
			render: p => <StatusBadge status={p.status} label={statusLabel(t, "purchases.statuses", p.status)} />,
			align: "center",
		},
		{
			key: "payment",
			label: t("reports.cols.paymentStatus"),
			value: p => statusLabel(t, "sales.paymentStatuses", p.paymentStatus),
			render: p => (
				<StatusBadge
					status={p.paymentStatus}
					label={statusLabel(t, "sales.paymentStatuses", p.paymentStatus)}
				/>
			),
			align: "center",
			hideOnMobile: true,
		},
		{
			key: "tax",
			label: t("reports.cols.tax"),
			value: p => num(p.taxAmount),
			kind: "money",
			currency,
			total: true,
			hideOnMobile: true,
		},
		{
			key: "total",
			label: t("reports.cols.total"),
			value: p => num(p.totalAmount),
			kind: "money",
			currency,
			total: true,
		},
	];
}

export const loadPurchases = (params: ReportFiltersParams) =>
	reportsAPI.getPurchasesReport(params).then((d: any) => (Array.isArray(d) ? d : []) as any[]);

export function PurchasesReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery("purchases", params, () => loadPurchases(params));
	return (
		<ReportState query={query}>
			{rows => {
				const active = rows.filter(p => p.status !== "cancelled");
				const total = sumBy(active, p => num(p.totalAmount));
				const unpaid = active.filter(p => p.paymentStatus !== "paid");
				const bySupplier = new Map<string, number>();
				for (const p of active) {
					const name = p.supplier?.name || t("reports.noSupplier");
					bySupplier.set(name, (bySupplier.get(name) ?? 0) + num(p.totalAmount));
				}
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={Truck}
								label={t("reports.kpi.purchasesTotal")}
								value={total}
								format="money"
								currency={currency}
								hint={t("reports.kpi.excludesCancelled")}
							/>
							<StatTile icon={FileText} label={t("reports.kpi.purchaseOrders")} value={active.length} />
							<StatTile
								icon={ShoppingBag}
								label={t("reports.kpi.received")}
								value={active.filter(p => p.status === "received").length}
								hint={t("reports.kpi.ofTotal", { count: active.length })}
							/>
							<StatTile
								icon={Wallet}
								label={t("reports.kpi.unpaid")}
								value={sumBy(unpaid, p => Math.max(0, num(p.totalAmount) - num(p.paidAmount)))}
								format="money"
								currency={currency}
								tone={unpaid.length ? "warning" : "default"}
								hint={t("reports.kpi.unpaidHint", { count: unpaid.length })}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.purchasesBySupplier")}
							currency={currency}
							showShare
							items={Array.from(bySupplier.entries()).map(([name, value]) => ({
								key: name,
								label: name,
								value,
							}))}
						/>
						<ReportTable
							columns={purchaseColumns(t, currency)}
							rows={rows}
							rowKey={p => p.id}
							rowHref={p => `/purchases/${p.id}`}
							exportName={exportName}
							emptyIcon={Truck}
							defaultSort={{ key: "date", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.purchases")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}
